import { User, UserManager, SignoutResponse, UserManagerSettings } from 'oidc-client';
import { authActions } from '../../../reducers/authReducer/authSlice';
import { UserPermission } from '../../../enum/userPermissions';
import EnvWindow from '../../../utils/env';
import { AppDispatch } from '../../../store';
import axios from 'axios';
import { AccountTypeEnum, UserPasswordStatus } from '../../../models/user.types';

const initConfig: UserManagerSettings = {
  authority: EnvWindow.REACT_APP_AUTHORITY_URL,
  client_id: 'ckeone_react',
  redirect_uri: EnvWindow.REACT_APP_REDIRECT_URL,
  silent_redirect_uri: EnvWindow.REACT_APP_SILENT_URL,
  response_type: 'code',
  scope: 'offline_access openid profile api1 role assessmentSession',
  post_logout_redirect_uri: EnvWindow.REACT_APP_POST_LOGOUT_REDIRECT_URL,
  loadUserInfo: true,
  automaticSilentRenew: false,
  filterProtocolClaims: true,
}

const userManager = new UserManager(initConfig);

// Set up events after userManager is created
userManager.events.addSilentRenewError((error: Error) => {
  console.error('Silent renewal failed:', error);

  if (error.message && error.message.includes('browserLink')) {
    console.warn('Ignoring Browser Link error - this is a development tool issue');
    return;
  }

  if (error.message.includes('login_required') || error.message.includes('interaction_required')) {
    userManager.removeUser().then(() => {
      localStorage.clear();
      window.location.href = '/logged-out';
    }).catch((err) => console.error('Error removing user:', err));
  }
});

export async function loadUserFromStorage(
  dispatch: AppDispatch,
  assessmentSessionId?: string,
  setUser?: (user: User) => void,
  asExaminer?: boolean,
): Promise<void> {
  try {
    // Don't recreate UserManager - it loses event handlers from AuthProvider
    // Just use the existing instance and call signinSilent if needed
    
    let user;
    if (assessmentSessionId) {
      await userManager.signinSilent({ assessmentSessionId: assessmentSessionId, asExaminer: asExaminer }).then(async () => {
        user = await userManager.getUser();
        if (user && setUser) {
          setUser(user);
          dispatch(authActions.storeUserAndSetAuthHeader(user));
        }
      });

    } else {
      user = await userManager.getUser();
    }
    if (!user) {
      dispatch(authActions.storeUserError());
    } else {
      dispatch(authActions.storeUserAndSetAuthHeader(user));
    }
  } catch (e) {
    console.error(`Error in loadUserFromStorage: ${e}`);
    dispatch(authActions.storeUserError());
  }
  return;
}

export function signinRedirect(): Promise<void> {
  // Clear any stale state before redirecting to prevent URL accumulation
  return userManager.clearStaleState()
    .catch((error) => {
      console.warn('Failed to clear stale state before redirect. Error type:', error?.name || 'Unknown');
      // Continue with redirect even if cleanup fails
    })
    .then(() => userManager.signinRedirect())
    .catch((error) => {
      console.error('Sign-in redirect failed. Error type:', error?.name || 'Unknown');
      // Attempt recovery by clearing state and retrying once
      return userManager.clearStaleState()
        .then(() => userManager.signinRedirect())
        .catch((retryError) => {
          console.error('Sign-in redirect retry failed. Error type:', retryError?.name || 'Unknown');
          throw retryError;
        });
    });
}

export function signinRedirectCallback(): Promise<User> {
  return userManager.signinRedirectCallback().catch((error) => {
    console.error('Sign-in callback error. Type:', error?.name || 'Unknown', 'Message:', error?.message || 'No details');
    // Clear stale state to prevent loops on error
    userManager.clearStaleState().catch((clearError) => {
      console.warn('Failed to clear stale state after callback error. Error type:', clearError?.name || 'Unknown');
    });
    throw error;
  });
}

export function signoutRedirect(): Promise<void> {
  userManager.clearStaleState();
  userManager.removeUser();
  return userManager.signoutRedirect();
}

export function signoutRedirectCallback(): Promise<SignoutResponse> {
  userManager.clearStaleState();
  userManager.removeUser();
  return userManager.signoutRedirectCallback();
}

export function hasPermission(user: User, permissionName: UserPermission | UserPermission[]): boolean {

  if (typeof permissionName === 'string') {
    if (user && user.profile.permission) {
      if (user.profile.permission.includes(permissionName)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } else {
    if (user && user.profile.permission) {
      const containsAll = permissionName.every((element) => {
        return user.profile.permission.indexOf(element) !== -1;
      });
      return containsAll;
    } else {
      return false;
    }
  }
}
export function userOkeId(user: User) {
  return user.profile.oke_id;
}

export const hasOnePermissionToAccess = (user: User, permission: UserPermission | UserPermission[]): boolean => {
  if (permission !== undefined) {
    if (typeof permission === 'string') {
      return hasPermission(user, permission);
    } else {
      return permission.some((p) => hasPermission(user, p));
    }
  } else return true;
};

export function silentRenewal(): Promise<User> {
  return userManager.signinSilent();
}


export function silentRenewalCallback(): Promise<void | User | undefined> {
  return userManager.signinSilentCallback().catch((e) => console.log(e));
}

export async function getPasswordStatus(): Promise<UserPasswordStatus> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/User/password/status`);
  return response.data
}

export function getUserManager(): UserManager {
  return userManager;
}
export function getUserAccountTypeId(user: User): AccountTypeEnum {
  const accountType: string = user?.profile?.account_type;
  switch (accountType) {
    case 'examcommitteeemployee':
      return AccountTypeEnum.ExamCommitteeEmployee;
    case 'examiner':
      return AccountTypeEnum.Examiner;
    case 'administrator':
      return AccountTypeEnum.Administrator;
    default:
      throw new Error(`Unknown account type: ${accountType}`);
  }
}

export default userManager;
